import jwtDecode from 'jwt-decode'
import { setToken, unsetToken } from '~/plugins/util/auth'

let selfKillInterval
let retryTimeout
let killed

const fetchUserMaxTime = 2 * 60 * 60 * 1000 // 2h

export const state = () => ({
  user: null,
  tokenMetadata: null,
  locked: false,
  fetchUserLastTimestamp: null,
})

export const mutations = {
  setUser: (state, user) => (state.user = user),
  setLock: (state, reason) => {
    const locked = reason !== false
    state.locked = locked
  },
  setTokenMetadata: (state, tokenMetadata) =>
    (state.tokenMetadata = tokenMetadata),
  setFetchUserLastTimestamp: (state, time) =>
    (state.fetchUserLastTimestamp = time),
}

export const actions = {
  async getUser({ state, commit }, staleUser) {
    if (!navigator.onLine) {
      if (!state.user) commit('setUser', staleUser)
      return
    }

    const now = Date.now()
    if (
      !state.user ||
      !state.fetchUserLastTimestamp ||
      state.fetchUserLastTimestamp + fetchUserMaxTime < now
    ) {
      try {
        const { data: user } = await this.$axios.get('/v3/app/user/me')
        commit('setUser', user)
        commit('setFetchUserLastTimestamp', now)
      } catch (error) {
        console.error('Error getting user info, using stale information')
        if (!state.user) commit('setUser', staleUser)
      }
    }
  },
  async authenticate({ dispatch, commit }, token) {
    if (!token) return
    const { data: staleUser, ...tokenMetadata } = jwtDecode(token)
    commit('setTokenMetadata', tokenMetadata)
    this.$axios.$setToken(token)
    setToken(token)
    dispatch('getUser', staleUser)
  },
  async recoveryRequest(_, payload) {
    try {
      const response = await this.$axios.post(
        '/v2/auth/app/recovery/request',
        payload
      )
      $nuxt.$telemetry.pushEvent({
        category: 'auth',
        name: 'passwordRecovery',
        action: 'request',
        payload,
      })
      return response.data
    } catch (error) {
      $nuxt.$telemetry.pushEvent({
        category: 'auth',
        name: 'passwordRecovery',
        action: 'request',
        error,
        payload,
      })
      throw error
    }
  },
  async recoveryResponse(_, payload) {
    try {
      const response = await this.$axios.post(
        '/v2/auth/app/recovery/response',
        payload
      )
      $nuxt.$telemetry.pushEvent({
        category: 'auth',
        name: 'passwordRecovery',
        action: 'response',
        payload,
      })
      return response.data
    } catch (error) {
      $nuxt.$telemetry.pushEvent({
        category: 'auth',
        name: 'passwordRecovery',
        action: 'response',
        error,
        payload,
      })
      throw error
    }
  },
  async clearStorage({ commit }) {
    commit(
      'hierarchy/UPDATE_ROOT',
      { data: null, lastSyncDate: null },
      { root: true }
    )
    commit(
      'portfolio/UPDATE_ROOT',
      { data: null, lastSyncDate: null },
      { root: true }
    )
    commit(
      'clientOptions/UPDATE_ROOT',
      { categories: null, lastSyncDate: null },
      { root: true }
    )
    // commit(
    //   'research/RESET',
    //   null,
    //   { root: true }
    // )
  },
  async login({ dispatch }, payload) {
    try {
      await dispatch('clearStorage')
      const response = await this.$axios.post(
        '/v2/auth/app/authenticate',
        payload
      )
      if (!response.data.session) throw new Error('Invalid response')
      await dispatch('authenticate', response.data.session)
      $nuxt.$telemetry.pushEvent({
        category: 'auth',
        name: 'login',
        result: 'success',
      })
      return response.data
    } catch (error) {
      $nuxt.$telemetry.pushEvent({
        category: 'auth',
        name: 'login',
        error,
      })
      throw error
    }
  },
  async refreshToken({ dispatch }) {
    try {
      const response = await this.$axios.post('/v2/auth/app/refresh')
      const { session } = response.data
      if (!session) throw new Error('Invalid response')
      await dispatch('authenticate', session)
      $nuxt.$telemetry.pushEvent({
        category: 'auth',
        name: 'refreshToken',
        result: 'success',
      })
      return session
    } catch (error) {
      $nuxt.$telemetry.pushEvent({
        category: 'auth',
        name: 'refreshToken',
        error,
      })
      throw error
    }
  },
  async register({ dispatch }, payload) {
    const response = await this.$axios.post('/auth/register', payload)
    if (!response.data.session) throw new Error('Invalid response')
    await dispatch('authenticate', response.data.session)
  },
  async logout({ commit, rootState, dispatch, state }, options = {}) {
    if (!state.user || killed) return
    if (retryTimeout && retryTimeout !== options.retryKey) return
    const {
      silent = false,
      forced = false,
      tryRevalidation = false,
      reason,
    } = options
    if (navigator.offline) {
      if (!silent)
        $nuxt.$notify.info({
          message: 'Não é possível fazer logout offline',
        })
      if (!forced)
        $nuxt.$telemetry.pushEvent({
          category: 'auth',
          name: 'logout',
          action: 'triedOffline',
          result: 'error',
          options,
        })
      return
    }
    const { current, offlineSync } = rootState.coaching
    const { $nuxt } = window
    if (!$nuxt) {
      console.log('Nuxt not loaded')
      let retryKey
      retryKey = setTimeout(
        () => dispatch('logout', { ...options, retryKey }),
        1000
      )
      retryTimeout = retryKey
      return
    }
    if (forced && tryRevalidation) {
      try {
        await dispatch(
          'layout/getUserInfos',
          { throwError: !silent },
          { root: true }
        )
        if (selfKillInterval) {
          clearInterval(selfKillInterval)
          selfKillInterval = null
        }
        $nuxt.$telemetry.pushEvent({
          category: 'auth',
          name: 'logout',
          action: 'unlockForced',
          options,
        })
        commit('setLock', false)
        killed = false
        /*
        $nuxt.$notify.info({
          message: 'Sua autenticação foi reestabelecida',
        })
        */
        return
      } catch (error) {
        $nuxt.$telemetry.pushEvent({
          category: 'auth',
          name: 'logout',
          action: 'unlockForced',
          error,
          options,
        })
        if (!state.locked) commit('setLock', reason)
        commit('setLock', 'Retry connection failed')
      }
    }
    if (current || Object.keys(offlineSync).length) {
      if (state.locked) {
        console.log('Self kill')
        if (!selfKillInterval)
          selfKillInterval = setInterval(
            () => dispatch('logout', options),
            2000
          )
        return
      }
      commit('setLock', reason || 'Unknown')
      $nuxt.$telemetry.pushEvent({
        category: 'auth',
        name: 'logout',
        action: 'lock',
        options,
      })
      if (!silent)
        $nuxt.$notify.info({
          message:
            'Não é possível fazer logout durante um processo de coaching',
        })
      else console.log('Ignorando logout forçado silenciosamente')
      return false
    }
    if (selfKillInterval) {
      clearInterval(selfKillInterval)
      selfKillInterval = null
    }
    if (forced) {
      $nuxt.$router.push('/locked')
      killed = true
    }
    $nuxt.$telemetry.pushEvent({
      name: 'logout',
      category: 'auth',
      action: 'finalTelemetry',
      result: 'queued',
      options,
    })
    const telemetry = $nuxt
      .$telemetry(true)
      .catch(error => console.error(error))
    if (forced)
      await $nuxt
        .$confirm(
          'Sua autenticação expirou, por favor, faça login novamente.',
          'Atenção',
          { showCancelButton: false }
        )
        .catch(() => null)
    await telemetry.catch(() => null)
    $nuxt.$api.services.server.$kill()
    commit(
      'coaching/UPDATE_ROOT',
      { current: null, offlineSync: {}, lastCoaching: null },
      { root: true }
    )
    commit('forms/UPDATE_ROOT', { available: null, hash: null }, { root: true })
    commit(
      'routePlan/UPDATE_ROOT',
      { available: null, hash: null },
      { root: true }
    )
    await dispatch('clearStorage')
    commit('setUser', null)
    commit('setLock', false)
    $nuxt.$axios.$setToken(null)
    unsetToken()
    localStorage.clear('vuex')
    localStorage.clear('token')
    $nuxt.$router.push('/login')
    window.location.reload(true)
    return true
  },
}

export const getters = {
  isAuthenticated: state => Boolean(state.user),
}
